import React, { useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import ReactAudioPlayer from "react-audio-player";

import { selectUserState } from "@/store/slices/user.slice";
import { useGetAllStages } from "@/firebase/stages/use.get.all.stages";
import { useAppSelector } from "@/hooks/common";
import { BodyText } from "@/components/body.text";
import { Impressum } from "@/components/impressum";
import { CoverImage } from "@/components/cover.image";
import { StyledButton } from "@/components/button";
import { AppRoutes } from "@/constants/app.routes";
import { getAudios } from "@/constants/audios";
import { SelectedDifficulty } from "@/models/shared";
import { getBatchConfig } from "@/utils/get-batch-config";

import StageCard from "./stage.card";
import { ContentWrapper, PoliceServerCredentials } from "./styles";

export const SelectStage: React.FC = () => {
  const user = useAppSelector(selectUserState).user!;
  const { stages, loading: stagesLoading } = useGetAllStages();

  // Get batch-specific audio files
  const batchAudios = getAudios();

  // Get batch configuration to check if multiple envelopes exist
  const batchConfig = getBatchConfig(user?.selected_batch);
  const hasMultipleEnvelopes = batchConfig?.hasMultipleEnvelopes ?? true; // Default to true for backward compatibility

  // --- NEW: derive difficulty + completion/envelope state --------------------
  const selectedDifficulty = user.selected_difficulty as SelectedDifficulty | undefined;
  const solvedStageNumbers: number[] = useMemo(
    () => (Array.isArray(user.solved_stages) ? user.solved_stages : []),
    [user.solved_stages]
  );

  const isStageSolved = useCallback(
    (stage: any) => typeof stage?.stage_number === "number" && solvedStageNumbers.includes(stage.stage_number),
    [solvedStageNumbers]
  );

  // 1) Difficulty filtering via stage.eligible_difficulty: string[]
  const difficultyFilteredStages = React.useMemo(() => {
    if (!stages) return [];
    if (!selectedDifficulty) return stages; // fallback: show all if no selection yet
    return stages.filter((s: any) => {
      const arr = s?.eligible_difficulty;
      if (!Array.isArray(arr) || arr.length === 0) return true; // backward compatible
      return arr.includes(selectedDifficulty);
    });
  }, [stages, selectedDifficulty]);

  // 2) Envelope lock: envelope 2 locked until all envelope 1 stages solved
  const envelope1Stages = React.useMemo(
      () => difficultyFilteredStages.filter((s: any) => (s?.envelope ?? 1) === 1),
      [difficultyFilteredStages]
  );
  const envelope2Stages = React.useMemo(
      () => difficultyFilteredStages.filter((s: any) => (s?.envelope ?? 1) === 2),
      [difficultyFilteredStages]
  );

  const isEnvelope1Complete = React.useMemo(() => {
    if (envelope1Stages.length === 0) return false; // only "complete" if there actually are envelope 1 stages
    return envelope1Stages.every(isStageSolved);
  }, [envelope1Stages, isStageSolved]);

  const lockEnvelope2 = !isEnvelope1Complete;

  return (
      <ContentWrapper>
        {stagesLoading === "loaded" && (
            <>
              <CoverImage />

              {/* Audio: So wird gespielt - only show if audio file is defined */}
              {batchAudios.how_to_play && (
                <Box mt={3.2} mb={4} textAlign="center">
                  <Typography variant="h6" gutterBottom>
                    Tutorial: So wird gespielt
                  </Typography>
                  <ReactAudioPlayer src={batchAudios.how_to_play} controls controlsList="nodownload" />
                </Box>
              )}

              <PoliceServerCredentials>
                <h3>Login Polizei Server</h3>
                <p>
                  Nutzer: <strong>{user.cc_police_server_username}</strong>
                </p>
                <p>
                  Passwort: <strong>{user.cc_police_server_password}</strong>
                </p>
                <p>
                  Adresse:{" "}
                  <a
                      href="https://polizei.gretenburg.de"
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ color: "inherit" }}
                  >
                    <strong>polizei.gretenburg.de</strong>
                  </a>
                </p>
              </PoliceServerCredentials>

              {/* Stages (difficulty-filtered + envelope lock) */}
              <Box my={3.2}>
                {/* Envelope 1 first */}
                {envelope1Stages.map((stage: any) => (
                    <Box my={3.2} key={stage.id}>
                      <StageCard stage={stage} />
                    </Box>
                ))}


                {/* 3) NEW: Envelope 2 intro audio once envelope 1 is finished - only for multi-envelope batches */}
                {hasMultipleEnvelopes && isEnvelope1Complete && batchAudios.intro_envelope_two && (
                    <Box mt={2} mb={4} textAlign="center">
                      <Typography variant="h6" gutterBottom>
                        Zwischenbericht Kommissar Hecklmeier (Einleitung zu Umschlag 2)
                      </Typography>
                      <ReactAudioPlayer src={batchAudios.intro_envelope_two} controls controlsList="nodownload" />
                    </Box>
                )}


                {/* Envelope 2 — greyed/locked until envelope 1 complete */}
                {envelope2Stages.map((stage: any) => {
                  const locked = lockEnvelope2;
                  return (
                      <Box key={stage.id} my={3.2} position="relative">
                        {locked && (
                            <Box
                                aria-hidden
                                sx={{
                                  position: "absolute",
                                  inset: 0,
                                  bgcolor: "rgba(0,0,0,0.35)",
                                  zIndex: 1,
                                  borderRadius: 2,
                                  pointerEvents: "auto",
                                }}
                            />
                        )}
                        <Box sx={{ opacity: locked ? 0.6 : 1, pointerEvents: locked ? "none" : "auto" }}>
                          <StageCard stage={stage} />
                        </Box>
                        {locked && (
                            <Box
                                sx={{
                                  position: "absolute",
                                  left: "50%",
                                  top: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 2,
                                  px: 2,
                                  py: 1,
                                  bgcolor: "background.paper",
                                  borderRadius: 2,
                                  boxShadow: 3,
                                  textAlign: "center",
                                  maxWidth: 360,
                                  width: "90%",
                                }}
                            >
                              <Typography variant="subtitle2">🔒 Umschlag 2 ist gesperrt</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Beantworte zuerst alle Fragen in Umschlag 1.
                              </Typography>
                            </Box>
                        )}
                      </Box>
                  );
                })}
              </Box>

              {user.completed_game && (
                  <Box mb={3.2} textAlign="center">
                    <StyledButton component={Link} to={AppRoutes.stages_complete} variant="contained">
                      Zum Abschluss Audio
                    </StyledButton>
                  </Box>
              )}


              <Box mt={3.2} mb={1} textAlign="center">
                <Impressum />
              </Box>

            </>
        )}

        {stagesLoading === "loading" && (
            <Box mt={6.4} textAlign="center">
              <CircularProgress />
            </Box>
        )}

        {stagesLoading === "failed" && <BodyText mt={6.4}>Oops! Something went wrong</BodyText>}
      </ContentWrapper>
  );
};
